'use client';

import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

let rumInitialized = false;

export function resetRumStateForTesting() {
  rumInitialized = false;
}

function sanitizeSampleRate(rate: number): number {
  if (Number.isNaN(rate) || rate < 0) return 0;
  if (rate > 100) return 100;
  return rate;
}

const FILTERED_RESOURCE_URL_PATTERNS = ['segment.io', 'segment.com'];

// Benign browser noise: the ResizeObserver "loop" errors mean the browser
// throttled observer notifications for a frame; nothing is broken and there
// is no app stack to act on.
const FILTERED_ERROR_MESSAGE_PATTERNS = [
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
];

// Errors thrown by code injected from browser extensions (e.g. MetaMask)
// are outside our control and pollute error tracking.
const FILTERED_ERROR_STACK_PATTERNS = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
];

export function beforeSend(event: RumEvent): boolean {
  if (event.type === 'resource') {
    const url = event.resource.url;
    if (
      FILTERED_RESOURCE_URL_PATTERNS.some((pattern) => url.includes(pattern))
    ) {
      return false;
    }
  }
  if (event.type === 'error') {
    const message = event.error?.message ?? '';
    const stack = event.error?.stack ?? '';
    if (
      FILTERED_ERROR_MESSAGE_PATTERNS.some((pattern) =>
        message.includes(pattern),
      ) ||
      FILTERED_ERROR_STACK_PATTERNS.some((pattern) => stack.includes(pattern))
    ) {
      return false;
    }
  }
  return true;
}

export interface DatadogConfig {
  applicationId: string;
  clientToken: string;
  enabled: boolean;
  env: string;
  service: string;
  site: string;
  version: string;
  sessionSampleRate: number;
  traceSampleRate: number;
  allowedTracingUrls: string[];
}

interface DatadogContextType {
  isInitialized: boolean;
}

const DatadogContext = createContext<DatadogContextType>({
  isInitialized: false,
});

export const useDatadog = () => useContext(DatadogContext);

export function DatadogProvider({
  children,
  config,
}: {
  children: ReactNode;
  config: DatadogConfig;
}) {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!config.applicationId || !config.clientToken) {
      console.error('Datadog configuration is missing required fields');
      return;
    }

    // Only initialize if not already initialized
    if (!isInitialized && config.enabled && typeof window !== 'undefined') {
      if (rumInitialized) {
        setIsInitialized(true);
        return;
      }

      try {
        datadogRum.init({
          applicationId: config.applicationId,
          clientToken: config.clientToken,
          site: config.site,
          service: config.service,
          env: config.env,
          version: config.version,
          sessionSampleRate: sanitizeSampleRate(config.sessionSampleRate),
          sessionReplaySampleRate: 0, // Session Replay disabled — set > 0 to enable
          traceSampleRate: sanitizeSampleRate(config.traceSampleRate),
          trackingConsent: 'not-granted',
          traceContextInjection: 'sampled',
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          defaultPrivacyLevel: 'mask-user-input',
          allowedTracingUrls: config.allowedTracingUrls,
          beforeSend,
        });

        rumInitialized = true;
        setIsInitialized(true);
        console.debug('Datadog Browser RUM initialized');
      } catch (error) {
        console.error('Failed to initialize Datadog Browser RUM:', error);
      }
    }
  }, [config, isInitialized]);

  return (
    <DatadogContext.Provider value={{ isInitialized }}>
      {children}
    </DatadogContext.Provider>
  );
}
